import clsx from 'clsx'
import type moment from 'moment'
import { rem } from 'polished'
import { useCallback, useId, useState } from 'react'
import * as React from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'

import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import Modal from 'LuxKit/Modal/Modal'
import RadioInput from 'LuxKit/Radio/RadioInput'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { CANCELLATION_POLICIES } from 'constants/cancellationPolicies'
import config from 'constants/config'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useRebrandSuccessDarkColour from 'hooks/useRebrandSuccessDarkColour'
import type { FlashCancellationOptionRow } from 'lib/offer/flashCancellationOptions'

import FlashCancellationOptionPriceDifference from './FlashCancellationOptionPriceDifference'
import useFlashCancellationOptionCopy from './useFlashCancellationOptionCopy'
import useFlashCancellationOptionFacts, { type FlashCancellationOptionFacts } from './useFlashCancellationOptionFacts'

const messages = defineMessages({
  dateChanges: {
    id: 'iKV7zLntnVzuQXMVnI0X',
    defaultMessage: 'Date changes',
    description: 'Row label for the date changes each flash cancellation option allows, in the cancellation options comparison modal',
  },
  changeOfMind: {
    id: 'hUoRBWLqN3vNS9G7Ldz5',
    defaultMessage: 'Change of mind',
    description: 'Row label for the change of mind refund each flash cancellation option gives, in the cancellation options comparison modal',
  },
  whatYouGetBack: {
    id: 'xrrhBf_vW73jq4SgLVzK',
    defaultMessage: 'What you get back',
    description: 'Row label for how each flash cancellation option refunds a cancellation, in the cancellation options comparison modal',
  },
})

const FACT_KEYS: Array<keyof FlashCancellationOptionFacts> = ['dateChanges', 'changeOfMind', 'whatYouGetBack']

const Comparison = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-auto-flow: column;
    grid-template-rows: repeat(5, auto);
    grid-template-columns: max-content;
    grid-auto-columns: minmax(0, 1fr);
    column-gap: ${rem(12)};
    row-gap: 0;
  }
`

const LabelColumnCell = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    display: block;
    padding-block: ${rem(8)};

    &.spacer {
      grid-row: span 2;
    }
  }
`

const BadgeSlot = styled.div`
  &:empty {
    display: none;
  }

  ${mediaQueryUp.tablet} {
    padding-block-end: ${rem(8)};

    &:empty {
      display: block;
    }
  }
`

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  padding: ${rem(16)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.M};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  &.selected {
    border-color: ${props => props.theme.palette.highlight.primary.darkForeground};
  }

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-row: span 4;
    grid-template-rows: subgrid;
    gap: 0;
    padding-block: ${rem(8)};
  }
`

const CardHeader = styled.div`
  ${mediaQueryUp.tablet} {
    padding-block-end: ${rem(8)};
  }
`

const FactCell = styled.div`
  ${mediaQueryUp.tablet} {
    padding-block: ${rem(8)};
  }
`

const CardFactLabel = styled(BodyText)`
  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const FactValue = styled.div`
  display: flex;
  gap: ${rem(4)};
  align-items: flex-start;
`

interface CardProps {
  row: FlashCancellationOptionRow
  facts: FlashCancellationOptionFacts
  radioGroupName: string
  selected: boolean
  priceDifference: number
  factLabels: Record<keyof FlashCancellationOptionFacts, string>
  onSelect: (event: React.ChangeEvent<HTMLInputElement>) => void
}

function FlashCancellationOptionCard(props: CardProps) {
  const { row, facts, radioGroupName, selected, priceDifference, factLabels, onSelect } = props
  const intl = useIntl()
  const titleColour = useRebrandSuccessDarkColour()
  const getCopy = useFlashCancellationOptionCopy()
  const isNonRefundable = row.policyType === CANCELLATION_POLICIES.NON_REFUNDABLE

  return (
    <>
      <BadgeSlot>
        {row.isBase && <PromotionLabel>
          {intl.formatMessage({
            id: 'NgCXhZo22za3YxuZ2Kz5',
            defaultMessage: 'Recommended',
            description: 'Badge above the contracted cancellation option card in the flash cancellation options comparison modal',
          })}
        </PromotionLabel>}
      </BadgeSlot>
      <Card className={clsx({ selected })} data-testid="flash-cancellation-option-card">
        <CardHeader>
          <RadioInput name={radioGroupName} value={row.key} checked={selected} onChange={onSelect}>
            <Group direction="vertical" gap={2}>
              <BodyText
                variant="medium"
                weight="bold"
                colour={isNonRefundable ? undefined : titleColour}
              >
                {getCopy(row.policyType).title}
              </BodyText>
              <FlashCancellationOptionPriceDifference priceDifference={priceDifference} />
            </Group>
          </RadioInput>
        </CardHeader>
        {FACT_KEYS.map(factKey => (
          <FactCell key={factKey}>
            <CardFactLabel variant="small" weight="bold" colour="light-foreground-alt">{factLabels[factKey]}</CardFactLabel>
            <FactValue>
              {facts[factKey].positive
                ? <LineCheckIcon size="S" colour={titleColour} />
                : <LineTimesIcon size="S" colour="neutral-two" />}
              <BodyText variant="medium" colour={facts[factKey].positive ? undefined : 'neutral-two'}>{facts[factKey].content}</BodyText>
            </FactValue>
          </FactCell>
        ))}
      </Card>
    </>
  )
}

interface Props {
  rows: Array<FlashCancellationOptionRow>
  checkIn?: string | moment.Moment
  propertyTimezoneOffset?: number
  numberOfDateChangesAllowed?: string
  onChange: (key: string) => void
}

function FlashCancellationOptionsModal(props: Props) {
  const { rows, checkIn, propertyTimezoneOffset, numberOfDateChangesAllowed, onChange } = props
  const intl = useIntl()
  const modalContext = useModalElementContext()
  const radioGroupName = useId()
  const [pendingKey, setPendingKey] = useState(() => rows.find(row => row.isSelected)?.key ?? rows[0]?.key)
  const facts = useFlashCancellationOptionFacts({ rows, checkIn, propertyTimezoneOffset, numberOfDateChangesAllowed })
  const pendingPrice = rows.find(row => row.key === pendingKey)?.price ?? 0

  const factLabels: Record<keyof FlashCancellationOptionFacts, string> = {
    dateChanges: intl.formatMessage(messages.dateChanges),
    changeOfMind: intl.formatMessage(messages.changeOfMind),
    whatYouGetBack: intl.formatMessage(messages.whatYouGetBack),
  }

  const onSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPendingKey(event.target.value)
  }, [])

  const onClose = useCallback(() => {
    modalContext.resolve()
  }, [modalContext])

  const onApply = useCallback(() => {
    if (pendingKey) {
      onChange(pendingKey)
    }
    modalContext.resolve()
  }, [pendingKey, onChange, modalContext])

  return (
    <Modal
      title={intl.formatMessage({
        id: 'stPwC5S6M-aRyWPZySH4',
        defaultMessage: 'Cancellation options',
        description: 'Title of the modal comparing the cancellation options sold on a flash hotel package',
      })}
      subtitle={intl.formatMessage({
        id: 'r4VPDcDWW00B8qkFHXvq',
        defaultMessage: 'More flexibility, more peace of mind.',
        description: 'Subtitle of the modal comparing the cancellation options sold on a flash hotel package',
      })}
      primaryActionText={intl.formatMessage({
        id: 'temEgpNMBoU1fcdZtAC6',
        defaultMessage: 'Apply',
        description: 'Button in the flash cancellation options comparison modal that selects the chosen option and closes the modal',
      })}
      onPrimaryActionClick={onApply}
      secondaryActionText={intl.formatMessage({
        id: 'Hv2-STfrTAyI6Q0ep35O',
        defaultMessage: 'Close',
        description: 'Button in the flash cancellation options comparison modal that closes it without changing the selected option',
      })}
      onSecondaryActionClick={onClose}
    >
      <Group direction="vertical" gap={24}>
        <Comparison
          role="radiogroup"
          aria-label={intl.formatMessage({
            id: 'wZGRPcpDtKXlnCZwyoqC',
            defaultMessage: 'Choose a cancellation option',
            description: 'Accessible label for the radio group of cancellation option cards in the flash cancellation options comparison modal',
          })}
        >
          <LabelColumnCell className="spacer" />
          {FACT_KEYS.map(factKey => (
            <LabelColumnCell key={factKey}>
              <BodyText variant="medium" weight="bold">{factLabels[factKey]}</BodyText>
            </LabelColumnCell>
          ))}
          {rows.map(row => (
            <FlashCancellationOptionCard
              key={row.key}
              row={row}
              facts={facts.get(row.key)!}
              radioGroupName={radioGroupName}
              selected={row.key === pendingKey}
              priceDifference={row.key === pendingKey ? 0 : row.price - pendingPrice}
              factLabels={factLabels}
              onSelect={onSelect}
            />
          ))}
        </Comparison>
        <div>
          <BodyText variant="small" weight="bold">
            {intl.formatMessage({
              id: 'Ex1BuMx9V6BXNMrrrlmV',
              defaultMessage: 'Date changes & credit refunds',
              description: 'Heading of the terms note at the bottom of the flash cancellation options comparison modal',
            })}
          </BodyText>
          <BodyText variant="small">
            {intl.formatMessage({
              id: '1fIczY-9juevGUwhgsZq',
              defaultMessage: 'Deadlines end at 11:59 pm on the date shown. Date changes depend on availability. Surcharges may apply (peak periods or property-specific). {brandName} Credit is valid for 12 months.',
              description: 'Terms note at the bottom of the flash cancellation options comparison modal about deadlines, date change surcharges and credit validity',
            }, { brandName: config.brandConfig.name })}
          </BodyText>
        </div>
      </Group>
    </Modal>
  )
}

export default FlashCancellationOptionsModal
